/* Lasered With Love Co. theme JS
   Small vanilla custom elements, no dependencies:
   <menu-drawer>, <cart-drawer>, <product-form>, <product-gallery>,
   <quantity-stepper>, <product-recommendations>, <colour-preview>. */
(() => {
  'use strict';
  const routes = () => (window.theme && window.theme.routes) || {};
  const strings = () => (window.theme && window.theme.strings) || {};

  /* ---------- money ---------- */
  function formatMoney(cents) {
    const fmt = (window.theme && window.theme.moneyFormat) || '£{{amount}}';
    const value = (cents / 100).toFixed(2);
    const [whole, dec] = value.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec}`)
      .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${dec}`);
  }

  /* ---------- generic drawer behaviour ---------- */
  class Drawer {
    constructor(root, panelSelector) {
      this.root = root;
      this.panel = root.querySelector(panelSelector);
      this.openers = [];
      root.querySelectorAll('[data-drawer-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      root.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    open(trigger) {
      this.trigger = trigger || document.activeElement;
      this.root.setAttribute('open', '');
      this.root.setAttribute('aria-hidden', 'false');
      this.openers.forEach((b) => b.setAttribute('aria-expanded', 'true'));
      requestAnimationFrame(() => this.panel && this.panel.focus());
    }
    close() {
      if (!this.root.hasAttribute('open')) return;
      this.root.removeAttribute('open');
      this.root.setAttribute('aria-hidden', 'true');
      this.openers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (this.trigger && this.trigger.focus) this.trigger.focus();
    }
  }

  /* ---------- mobile menu ---------- */
  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      const root = this.querySelector('[data-drawer]');
      if (!root) return;
      this.drawer = new Drawer(root, '.drawer__panel');
      const opener = this.querySelector('[data-drawer-open]');
      if (opener) {
        this.drawer.openers.push(opener);
        opener.addEventListener('click', () => this.drawer.open(opener));
      }
      root.querySelectorAll('a[href]').forEach((a) => a.addEventListener('click', () => this.drawer.close()));
    }
  }
  customElements.define('menu-drawer', MenuDrawer);

  /* ---------- cart (Ajax API + Section Rendering API) ---------- */
  const SECTIONS = ['cart-drawer', 'cart-icon-bubble', 'main-cart'];

  async function cartRequest(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: SECTIONS.join(','), sections_url: window.location.pathname }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || strings().error || 'Something went wrong');
    return data;
  }

  function renderSections(sections) {
    if (!sections) return;
    const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
    if (sections['cart-drawer']) {
      const fresh = parse(sections['cart-drawer']).querySelector('#CartDrawerContent');
      const current = document.querySelector('#CartDrawerContent');
      if (fresh && current) current.innerHTML = fresh.innerHTML;
    }
    if (sections['cart-icon-bubble']) {
      const bubble = document.querySelector('#cart-icon-bubble');
      if (bubble) bubble.innerHTML = parse(sections['cart-icon-bubble']).body.innerHTML;
    }
    if (sections['main-cart']) {
      const fresh = parse(sections['main-cart']).querySelector('#MainCart');
      const current = document.querySelector('#MainCart');
      if (fresh && current) current.innerHTML = fresh.innerHTML;
    }
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  const Cart = {
    async add(formData) {
      const body = {};
      formData.forEach((v, k) => {
        // Rebuild properties[Name] into a nested object.
        const m = k.match(/^properties\[(.+)\]$/);
        if (m) { body.properties = body.properties || {}; if (String(v).trim()) body.properties[m[1]] = v; return; }
        if (k === 'options[' || k.startsWith('options[')) return; // option radios are UI only
        body[k] = v;
      });
      body.quantity = Number(body.quantity || 1);
      const data = await cartRequest(routes().cartAdd || '/cart/add.js', body);
      renderSections(data.sections);
      return data;
    },
    async change(key, quantity) {
      const data = await cartRequest(routes().cartChange || '/cart/change.js', { id: key, quantity });
      renderSections(data.sections);
      return data;
    },
    async clear() {
      const data = await cartRequest('/cart/clear.js', {});
      renderSections(data.sections);
      return data;
    },
    async note(note) {
      await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note }) });
    },
  };
  window.LWLCart = Cart;

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.drawer = new Drawer(this, '.drawer__panel');
      // Re-bind close buttons after each re-render.
      document.addEventListener('cart:updated', () => {
        this.querySelectorAll('[data-drawer-close]').forEach((el) => {
          if (el.dataset.bound) return;
          el.dataset.bound = '1';
          el.addEventListener('click', () => this.drawer.close());
        });
      });
      document.querySelectorAll('[data-cart-open]').forEach((btn) => {
        this.drawer.openers.push(btn);
        btn.addEventListener('click', (e) => {
          if (document.body.classList.contains('template-cart')) return; // on the basket page, follow the link
          e.preventDefault();
          this.drawer.open(btn);
        });
      });
    }
    open() { this.drawer.open(); }
  }
  customElements.define('cart-drawer', CartDrawer);

  // Quantity / remove / clear / note handlers (event delegation so re-rendered markup keeps working).
  let busy = false;
  const setBusy = (state) => {
    busy = state;
    document.querySelectorAll('#CartDrawer, #MainCart').forEach((el) => el.setAttribute('aria-busy', String(state)));
  };
  document.addEventListener('change', async (e) => {
    const input = e.target.closest('[data-cart-quantity]');
    if (!input || busy) return;
    setBusy(true);
    try { await Cart.change(input.dataset.key, Number(input.value)); } catch (err) { alertError(err); }
    setBusy(false);
  });
  document.addEventListener('click', async (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    const clear = e.target.closest('[data-cart-clear]');
    if ((!remove && !clear) || busy) return;
    setBusy(true);
    try { if (remove) await Cart.change(remove.dataset.key, 0); else await Cart.clear(); } catch (err) { alertError(err); }
    setBusy(false);
  });
  let noteTimer;
  document.addEventListener('input', (e) => {
    const note = e.target.closest('[data-cart-note]');
    if (!note) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => Cart.note(note.value), 600);
  });
  function alertError(err) {
    console.error(err);
    const region = document.querySelector('#CartDrawer .drawer__body, #MainCart');
    if (!region) return;
    const p = document.createElement('p');
    p.className = 'form-message form-message--error';
    p.setAttribute('role', 'alert');
    p.textContent = err.message || strings().error;
    region.prepend(p);
    setTimeout(() => p.remove(), 4000);
  }

  /* ---------- quantity stepper ---------- */
  class QuantityStepper extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('[data-step]').forEach((btn) => btn.addEventListener('click', () => {
        const step = Number(btn.dataset.step);
        const min = Number(this.input.min || 1), max = Number(this.input.max || 99);
        const next = Math.min(max, Math.max(min, Number(this.input.value || min) + step));
        if (next === Number(this.input.value)) return;
        this.input.value = next;
        this.sync();
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }));
      this.input.addEventListener('input', () => this.sync());
      this.sync();
    }
    sync() {
      const v = Number(this.input.value), min = Number(this.input.min || 1), max = Number(this.input.max || 99);
      const [dec, inc] = this.querySelectorAll('[data-step]');
      if (dec) dec.disabled = v <= min;
      if (inc) inc.disabled = v >= max;
    }
  }
  customElements.define('quantity-stepper', QuantityStepper);

  /* ---------- product form ---------- */
  // "Allowed characters" rules for personalisation boxes (snippets/product-attribute.liquid)
  const ATTR_RULES = {
    letters: /^[\p{L}\p{M}'’ -]*$/u,
    letters_numbers: /^[\p{L}\p{M}\p{N}'’ .,&!?-]*$/u,
    numbers: /^[\p{N} /.-]*$/u,
  };

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      const json = document.querySelector('[data-product-json]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.idInput = this.form.querySelector('[data-variant-id]');
      this.submit = this.form.querySelector('[data-submit]');
      this.submitLabel = this.form.querySelector('[data-submit-label]');
      this.submitPrice = this.form.querySelector('[data-submit-price]');
      this.error = this.form.querySelector('[data-form-error]');
      this.isPreorder = this.dataset.preorder === 'true';

      this.form.querySelectorAll('.option input[type="radio"]').forEach((r) => r.addEventListener('change', () => this.onOptionChange()));

      // Personalisation boxes and colour choices, saved on the order as properties[...]
      this.attrs = [...this.form.querySelectorAll('[data-attr]')];
      this.attrs.forEach((el) => {
        const input = el.querySelector('[data-attr-input]');
        const count = el.querySelector('[data-attr-count]');
        if (input) {
          const update = () => { if (count) count.textContent = input.value.length; };
          input.addEventListener('input', () => { update(); this.setAttrError(el, ''); });
          update();
        }
        const selected = el.querySelector('[data-attr-selected]');
        const checked = el.querySelector('input[type="radio"]:checked');
        if (selected && checked) selected.textContent = checked.value; // a colour that starts picked
        el.querySelectorAll('input[type="radio"]').forEach((r) => r.addEventListener('change', () => {
          if (selected) selected.textContent = r.value;
          this.setAttrError(el, '');
        }));
      });
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.onOptionChange(false);
    }

    selectedOptions() {
      return [...this.form.querySelectorAll('.option')].map((fs) => {
        const checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    currentVariant() {
      if (!this.variants.length) return null;
      const selected = this.selectedOptions();
      if (!selected.length) return this.variants[0];
      return this.variants.find((v) => v.options.every((o, i) => o === selected[i])) || null;
    }

    onOptionChange(updateUrl = true) {
      this.form.querySelectorAll('.option').forEach((fs) => {
        const checked = fs.querySelector('input:checked');
        const label = fs.querySelector('[data-option-value]');
        if (checked && label) label.textContent = checked.value;
      });
      const variant = this.currentVariant();
      this.variant = variant;
      if (variant) {
        this.idInput.value = variant.id;
        const price = document.querySelector('[data-price-current]');
        const compare = document.querySelector('[data-price-compare]');
        if (price) price.textContent = formatMoney(variant.price);
        if (compare) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) { compare.textContent = formatMoney(variant.compare_at_price); compare.hidden = false; }
          else compare.hidden = true;
        }
        if (updateUrl && window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        }
        if (variant.featured_media && variant.featured_media.id) {
          const gallery = document.querySelector('product-gallery');
          if (gallery && gallery.show) gallery.show(String(variant.featured_media.id));
        }
      }
      this.updateButton();
    }

    setAttrError(el, message) {
      const error = el.querySelector('[data-attr-error]');
      if (error) { error.textContent = message || ''; error.hidden = !message; }
      el.classList.toggle('attr--invalid', !!message);
    }

    // Checks every box and colour choice, shows a message under each problem and focuses the first one.
    validateAttrs() {
      let first = null;
      this.attrs.forEach((el) => {
        const required = el.dataset.attrRequired === 'true';
        const input = el.querySelector('[data-attr-input]');
        let message = '';
        if (input) {
          if (input.hasAttribute('data-attr-caps')) input.value = input.value.toUpperCase();
          const value = input.value.trim();
          const rule = ATTR_RULES[el.dataset.attrAllowed];
          if (required && !value) message = el.dataset.errorRequired;
          else if (value && rule && !rule.test(value.replace(/\s+/g, ' '))) message = el.dataset.errorAllowed;
        } else if (el.hasAttribute('data-attr-choice') && required && !el.querySelector('input:checked')) {
          message = el.dataset.errorRequired;
        }
        this.setAttrError(el, message);
        if (message && !first) first = el;
      });
      if (first) {
        first.scrollIntoView({ block: 'center', behavior: 'smooth' });
        const target = first.querySelector('[data-attr-input], input[type="radio"]');
        if (target) target.focus({ preventScroll: true });
      }
      return !first;
    }

    updateButton() {
      const s = strings();
      if (!this.variant) {
        this.submit.disabled = true;
        this.submitLabel.textContent = s.unavailable || 'Unavailable';
        this.submitPrice.textContent = '';
        return;
      }
      if (!this.variant.available) {
        this.submit.disabled = true;
        this.submitLabel.textContent = this.dataset.soldOut || s.soldOut || 'Sold out';
        this.submitPrice.textContent = '';
        return;
      }
      this.submit.disabled = false;
      this.submitLabel.textContent = this.isPreorder
        ? (this.dataset.preorderLabel || s.preorder || 'Pre-order')
        : (this.dataset.addToBasket || s.addToBasket || 'Add to basket');
      this.submitPrice.textContent = `· ${formatMoney(this.variant.price)}`;
    }

    async onSubmit(e) {
      e.preventDefault();
      if (this.submit.disabled) return;
      if (!this.validateAttrs()) return;
      this.error.hidden = true;
      this.submit.setAttribute('aria-busy', 'true');
      const original = this.submitLabel.textContent;
      try {
        await Cart.add(new FormData(this.form));
        this.submitLabel.textContent = this.dataset.added || strings().added || 'Added to basket';
        const drawer = document.querySelector('cart-drawer');
        if (drawer) drawer.open();
        setTimeout(() => { this.submitLabel.textContent = original; }, 1800);
      } catch (err) {
        this.error.textContent = err.message;
        this.error.hidden = false;
      }
      this.submit.removeAttribute('aria-busy');
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- product gallery ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.gallery__slide')];
      this.thumbs = [...this.querySelectorAll('.gallery__thumb')];
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.show(t.dataset.mediaId)));
    }
    show(id) {
      if (!this.slides.some((s) => s.dataset.mediaId === id)) return;
      this.slides.forEach((s) => { s.hidden = s.dataset.mediaId !== id; });
      this.thumbs.forEach((t) => t.setAttribute('aria-current', String(t.dataset.mediaId === id)));
      const slide = this.slides.find((s) => s.dataset.mediaId === id);
      const video = slide && slide.querySelector('video');
      if (video) video.play().catch(() => {});
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ---------- colour preview ----------
     Recolours parts of the first product photo as colours are picked. Each part has a mask the same size
     as the photo, white where the colour goes; the photo's own light and shade are kept on top of the
     new colour, so it still looks like the real thing. */
  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  function toRgb(colour) {
    const ctx = document.createElement('canvas').getContext('2d');
    ctx.fillStyle = colour;
    const hex = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(ctx.fillStyle);
    return hex ? hex.slice(1).map((h) => parseInt(h, 16)) : null;
  }

  class ColourPreview extends HTMLElement {
    connectedCallback() {
      this.parts = [...this.querySelectorAll('[data-part]')].map((el) => ({ choice: el.dataset.choice, mask: el.dataset.mask }));
      if (!this.parts.length) return;
      this.onChange = (e) => {
        const fieldset = e.target.matches('input[type="radio"]') && e.target.closest('fieldset[data-choice]');
        if (fieldset && this.parts.some((p) => p.choice === fieldset.dataset.choice)) this.render();
      };
      document.addEventListener('change', this.onChange);
      // Get the photo and masks ready in quiet time so the first pick shows straight away, and catch up
      // if the browser brought back colours picked before (going back to the page)
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
      idle(() => this.prepare().then(() => {
        if (this.parts.some((p) => this.picked(p.choice))) this.render(false);
      }).catch(() => {}));
    }

    // The colour picked for a part, or '' to leave it as photographed (nothing picked yet, or its photo colour)
    picked(choice) {
      const input = document.querySelector(`fieldset[data-choice="${CSS.escape(choice)}"] input:checked`);
      return input && !input.hasAttribute('data-in-photo') ? input.dataset.colour || '' : '';
    }

    disconnectedCallback() {
      if (this.onChange) document.removeEventListener('change', this.onChange);
    }

    // Loads the photo and masks once, and notes which pixels each part covers and how light they are
    prepare() {
      if (!this.ready) {
        this.ready = Promise.all([loadImage(this.dataset.photo), ...this.parts.map((p) => loadImage(p.mask))]).then(([photo, ...masks]) => {
          const w = photo.naturalWidth;
          const h = photo.naturalHeight;
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(photo, 0, 0);
          this.base = ctx.getImageData(0, 0, w, h); // throws if the photo can't be read, which leaves the preview off
          const px = this.base.data;
          this.regions = masks.map((mask, i) => {
            ctx.clearRect(0, 0, w, h);
            ctx.drawImage(mask, 0, 0, w, h);
            const m = ctx.getImageData(0, 0, w, h).data;
            const at = [];
            const cover = [];
            const light = [];
            for (let p = 0; p < m.length; p += 4) {
              const a = ((m[p] + m[p + 1] + m[p + 2]) / 765) * (m[p + 3] / 255);
              if (a < 0.02) continue;
              at.push(p);
              cover.push(a);
              light.push(0.2126 * px[p] + 0.7152 * px[p + 1] + 0.0722 * px[p + 2]);
            }
            // The part's flat face counts as the full colour; its edges and shadows come out darker
            const inside = light.filter((_, k) => cover[k] > 0.9).sort((a, b) => a - b);
            const face = inside[Math.floor(inside.length * 0.9)] || 255;
            return { choice: this.parts[i].choice, at, cover, shade: light.map((l) => l / face) };
          });
          this.canvas = canvas;
          this.ctx = ctx;
          this.appendChild(canvas);
        });
      }
      return this.ready;
    }

    async render(showPhoto = true) {
      try { await this.prepare(); } catch (err) { return; }
      const out = new ImageData(new Uint8ClampedArray(this.base.data), this.base.width, this.base.height);
      const d = out.data;
      let tinted = false;
      this.regions.forEach((r) => {
        const colour = this.picked(r.choice);
        const rgb = colour && toRgb(colour);
        if (!rgb) return;
        tinted = true;
        for (let k = 0; k < r.at.length; k++) {
          const p = r.at[k];
          const s = r.shade[k];
          for (let c = 0; c < 3; c++) {
            // Shade multiplies the colour like paint; anything brighter than the face (a glint) lifts towards white
            const v = s <= 1 ? rgb[c] * s : rgb[c] + (255 - rgb[c]) * Math.min(1, s - 1);
            d[p + c] += (v - d[p + c]) * r.cover[k];
          }
        }
      });
      this.ctx.putImageData(out, 0, 0);
      this.hidden = !tinted;
      const slide = this.closest('.gallery__slide');
      const gallery = this.closest('product-gallery');
      if (showPhoto && slide && gallery && gallery.show) gallery.show(slide.dataset.mediaId);
    }
  }
  customElements.define('colour-preview', ColourPreview);

  /* ---------- related products ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const url = this.dataset.url;
      if (!url) return;
      const load = () => fetch(url).then((r) => r.text()).then((html) => {
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('product-recommendations');
        if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
      }).catch(() => {});
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) { io.disconnect(); load(); } }, { rootMargin: '400px' });
        io.observe(this);
      } else load();
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- theme editor: open drawers when their section is selected ---------- */
  document.addEventListener('shopify:section:select', (e) => {
    const drawer = e.target.querySelector('cart-drawer');
    if (drawer) drawer.open();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const drawer = e.target.querySelector('cart-drawer');
    if (drawer) drawer.drawer.close();
  });
})();
